import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wand2,
  ChevronRight,
  ChevronLeft,
  Heart,
  User,
  DollarSign,
  Star,
  Loader2,
} from "lucide-react";

const OCCASIONS = [
  "Birthday",
  "Anniversary",
  "Wedding",
  "Graduation",
  "Christmas",
  "Valentine's Day",
  "Mother's Day",
  "Father's Day",
  "Baby Shower",
  "Housewarming",
  "Thank You",
  "Just Because",
  "Retirement",
  "Get Well Soon",
];

const INTERESTS = [
  "Reading",
  "Cooking",
  "Gaming",
  "Fitness",
  "Music",
  "Art",
  "Travel",
  "Photography",
  "Gardening",
  "Tech",
  "Fashion",
  "Sports",
  "Movies",
  "Yoga",
  "DIY Crafts",
  "Pets",
];

const SPORTS = [
  "Football",
  "Basketball",
  "Cricket",
  "Tennis",
  "Swimming",
  "Running",
  "Cycling",
  "Hiking",
  "Golf",
  "Soccer",
  "Baseball",
  "Volleyball",
];

const PERSONALITIES = [
  "Introvert",
  "Extrovert",
  "Adventurous",
  "Creative",
  "Practical",
  "Romantic",
  "Minimalist",
  "Luxurious",
  "Nerdy",
  "Sporty",
  "Eco-Conscious",
  "Foodie",
];

const LIFESTYLES = [
  "City Life",
  "Nature Lover",
  "Suburban",
  "Beach Vibes",
  "Mountain Explorer",
  "Traveler",
  "Homebody",
  "Workaholic",
];

export default function GiftInfoForm({ onSubmit, isLoading }) {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    recipientName: "",
    relationship: "",
    age: "",
    gender: "",
    occasion: "",
    budget: [50],
    interests: [],
    sports: [],
    personality: [],
    lifestyle: "",
    emotionalNote: "",
  });

  const toggleArrayItem = (field, item) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter((value) => value !== item)
        : [...prev[field], item],
    }));
  };

  const steps = [
    {
      title: "Who's the lucky person?",
      subtitle: "Tell us about the recipient",
      icon: User,
    },
    {
      title: "What's the occasion?",
      subtitle: "Help us set the mood",
      icon: Star,
    },
    {
      title: "Their world & interests",
      subtitle: "What makes them light up?",
      icon: Heart,
    },
    {
      title: "Anything else from the heart?",
      subtitle: "Your personal touch matters",
      icon: Wand2,
    },
  ];

  const canProceed = () => {
    if (step === 0) return formData.recipientName.trim() && formData.age;
    if (step === 1) return formData.occasion;
    return true;
  };

  const handleSubmit = () => {
    onSubmit(formData);
  };

  const StepIcon = steps[step].icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="giftInfoCard"
    >
      <div className="giftProgressWrap">
        {steps.map((_, index) => (
          <div key={index} className="giftProgressTrack">
            <motion.div
              className="giftProgressFill"
              initial={{ width: 0 }}
              animate={{ width: index <= step ? "100%" : "0%" }}
              transition={{ duration: 0.4 }}
            />
          </div>
        ))}
      </div>

      <div className="giftInfoBody">
        <div className="giftStepHeader">
          <div className="giftStepIcon">
            <StepIcon size={20} />
          </div>
          <div>
            <h2>{steps[step].title}</h2>
            <p>{steps[step].subtitle}</p>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div
              key="step0"
              initial={{ opacity: 0, x: 22 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -22 }}
              className="giftStepContent"
            >
              <div className="giftFormGrid">
                <div className="giftField">
                  <label>Their Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Sarah"
                    value={formData.recipientName}
                    onChange={(event) =>
                      setFormData({
                        ...formData,
                        recipientName: event.target.value,
                      })
                    }
                  />
                </div>

                <div className="giftField">
                  <label>Relationship</label>
                  <select
                    value={formData.relationship}
                    onChange={(event) =>
                      setFormData({
                        ...formData,
                        relationship: event.target.value,
                      })
                    }
                  >
                    <option value="">Select relationship</option>
                    {[
                      "Partner",
                      "Parent",
                      "Sibling",
                      "Friend",
                      "Colleague",
                      "Child",
                      "Grandparent",
                      "Teacher",
                      "Boss",
                      "Other",
                    ].map((relationship) => (
                      <option key={relationship} value={relationship}>
                        {relationship}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="giftFormGrid">
                <div className="giftField">
                  <label>Age</label>
                  <input
                    type="number"
                    placeholder="e.g. 28"
                    value={formData.age}
                    onChange={(event) =>
                      setFormData({ ...formData, age: event.target.value })
                    }
                  />
                </div>

                <div className="giftField">
                  <label>Gender</label>
                  <select
                    value={formData.gender}
                    onChange={(event) =>
                      setFormData({ ...formData, gender: event.target.value })
                    }
                  >
                    <option value="">Select gender</option>
                    {["Male", "Female", "Non-Binary", "Prefer not to say"].map(
                      (gender) => (
                        <option key={gender} value={gender}>
                          {gender}
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 22 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -22 }}
              className="giftStepContent"
            >
              <ChoiceGroup
                title="Occasion"
                items={OCCASIONS}
                selected={formData.occasion}
                onSelect={(occasion) =>
                  setFormData({ ...formData, occasion })
                }
              />

              <div className="giftBudgetBox">
                <div className="giftBudgetHeader">
                  <label>
                    <DollarSign size={16} />
                    Budget
                  </label>
                  <span>${formData.budget[0]}</span>
                </div>

                <input
                  className="giftRange"
                  type="range"
                  min="10"
                  max="500"
                  step="10"
                  value={formData.budget[0]}
                  onChange={(event) =>
                    setFormData({
                      ...formData,
                      budget: [Number(event.target.value)],
                    })
                  }
                />

                <div className="giftRangeLabels">
                  <span>$10</span>
                  <span>$250</span>
                  <span>$500</span>
                </div>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 22 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -22 }}
              className="giftStepContent"
            >
              <ChoiceGroup
                title="Interests"
                subtitle="pick multiple"
                items={INTERESTS}
                selected={formData.interests}
                multiple
                onSelect={(interest) => toggleArrayItem("interests", interest)}
              />

              <ChoiceGroup
                title="Sports They Enjoy"
                items={SPORTS}
                selected={formData.sports}
                multiple
                accent
                onSelect={(sport) => toggleArrayItem("sports", sport)}
              />

              <ChoiceGroup
                title="Personality"
                items={PERSONALITIES}
                selected={formData.personality}
                multiple
                onSelect={(personality) =>
                  toggleArrayItem("personality", personality)
                }
              />

              <ChoiceGroup
                title="Lifestyle"
                items={LIFESTYLES}
                selected={formData.lifestyle}
                accent
                onSelect={(lifestyle) =>
                  setFormData({ ...formData, lifestyle })
                }
              />
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 22 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -22 }}
              className="giftStepContent"
            >
              <div className="giftNoteBox">
                <p>
                  Share anything special — a memory, an inside joke, what they
                  mean to you. Our AI reads between the lines to find gifts that
                  truly resonate.
                </p>

                <textarea
                  placeholder="e.g. She's been talking about starting a garden on her balcony. She lost her favorite mug last month and was really sad about it. She loves sunsets and always takes photos of them..."
                  value={formData.emotionalNote}
                  onChange={(event) =>
                    setFormData({
                      ...formData,
                      emotionalNote: event.target.value,
                    })
                  }
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="giftFormNav">
          <button
            type="button"
            className="giftNavBtn giftNavGhost"
            onClick={() => setStep(step - 1)}
            disabled={step === 0}
          >
            <ChevronLeft size={16} />
            Back
          </button>

          {step < 3 ? (
            <button
              type="button"
              className="giftNavBtn giftNavPrimary"
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
            >
              Continue
              <ChevronRight size={16} />
            </button>
          ) : (
            <button
              type="button"
              className="giftNavBtn giftNavPrimary"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="giftSpin" />
                  Finding Gifts...
                </>
              ) : (
                <>
                  <Wand2 size={16} />
                  Discover Gifts
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function ChoiceGroup({
  title,
  subtitle,
  items,
  selected,
  onSelect,
  multiple = false,
  accent = false,
}) {
  const isActive = (item) => {
    return multiple ? selected.includes(item) : selected === item;
  };

  return (
    <div className="giftChoiceGroup">
      <h3>
        {title}
        {subtitle ? <span>({subtitle})</span> : null}
      </h3>

      <div className="giftChoiceList">
        {items.map((item) => (
          <button
            type="button"
            key={item}
            className={`giftChip ${isActive(item) ? "active" : ""} ${
              accent ? "accent" : ""
            }`}
            onClick={() => onSelect(item)}
          >
            {item}
          </button>
        ))}
      </div>
    </div>
  );
}